import { create } from 'zustand';
import type { LadderProgram } from '../../../../ladder/model/LadderProgram';
import type { LadderElement } from '../../../../ladder/model/LadderElement';
import { useProjectStore } from '../../../../store/projectStore';
import { compileLadderProgram, type CompileDiagnostic } from '../../../../ladder/compiler/compiler';

export interface EditorCommand {
  undoProgram: LadderProgram;
  redoProgram: LadderProgram;
}

export interface LadderEditorState {
  zoom: number;
  panX: number;
  panY: number;
  
  undoStack: EditorCommand[];
  redoStack: EditorCommand[];
  
  diagnostics: CompileDiagnostic[];
  // Selection
  selectedElementIds: string[];
  setSelectedElementIds: (ids: string[]) => void;
  addSelectedElementId: (id: string) => void;
  toggleSelectedElementId: (id: string) => void;
  clearSelection: () => void;

  // Active Tool
  activeTool: string | null;
  setActiveTool: (tool: string | null) => void;

  // Clipboard
  clipboard: LadderElement[];
  setClipboard: (elements: LadderElement[]) => void;

  setZoom: (zoom: number) => void;
  setPan: (panX: number, panY: number) => void;
  
  executeCommand: (newProgram: LadderProgram, prevProgram?: LadderProgram) => void;
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;
  
  compile: () => boolean;
}

export const useLadderEditorStore = create<LadderEditorState>()((set) => ({
  zoom: 1,
  panX: 0,
  panY: 0,
  
  selectedElementIds: [],
  activeTool: null,
  setActiveTool: (tool) => set({ activeTool: tool }),
  clipboard: [],

  undoStack: [],
  redoStack: [],
  
  diagnostics: [],
  
  setZoom: (zoom) => set({ zoom }),
  setPan: (panX, panY) => set({ panX, panY: Math.min(0, panY) }),
  
  setSelectedElementIds: (ids) => set({ selectedElementIds: ids }),
  addSelectedElementId: (id) => set((state) => ({ 
    selectedElementIds: state.selectedElementIds.includes(id) ? state.selectedElementIds : [...state.selectedElementIds, id] 
  })),
  toggleSelectedElementId: (id) => set((state) => ({
    selectedElementIds: state.selectedElementIds.includes(id) 
      ? state.selectedElementIds.filter((i: string) => i !== id) 
      : [...state.selectedElementIds, id]
  })),
  clearSelection: () => set({ selectedElementIds: [] }),

  setClipboard: (elements) => set({ clipboard: elements }),

  executeCommand: (newProgram, prevProgram) => {
    const currentProgram = prevProgram ?? useProjectStore.getState().project.plc.program;
    useProjectStore.getState().setProgram(newProgram);
    
    set((state) => ({
      undoStack: [...state.undoStack, { undoProgram: currentProgram, redoProgram: newProgram }],
      redoStack: []
    }));
  },
  
  undo: () => {
    set((state) => {
      if (state.undoStack.length === 0) return state;
      const cmd = state.undoStack[state.undoStack.length - 1];
      if (!cmd) return state;
      const newUndoStack = state.undoStack.slice(0, -1);
      
      useProjectStore.getState().setProgram(cmd.undoProgram);
      
      return {
        undoStack: newUndoStack,
        redoStack: [...state.redoStack, cmd]
      };
    });
  },
  
  redo: () => {
    set((state) => {
      if (state.redoStack.length === 0) return state;
      const cmd = state.redoStack[state.redoStack.length - 1];
      if (!cmd) return state;
      const newRedoStack = state.redoStack.slice(0, -1);
      
      useProjectStore.getState().setProgram(cmd.redoProgram);
      
      return {
        undoStack: [...state.undoStack, cmd],
        redoStack: newRedoStack
      };
    });
  },
  
  clearHistory: () => set({ undoStack: [], redoStack: [] }),
  
  compile: () => {
    const program = useProjectStore.getState().project.plc.program;
    const res = compileLadderProgram(program);
    set({ diagnostics: res.diagnostics });
    
    // For now we just compile, later we will load it to RuntimeHost
    return res.success;
  }
}));
